/**
 * The navigation's structure: which pages the sidebar and the Settings area
 * list, under which group, and who sees them. The backend enforces every rule
 * again; hiding a link only keeps the navigation honest about what someone
 * can open.
 */

import {
  BookOpen,
  ChartColumn,
  Eye,
  House,
  Info,
  type LucideIcon,
  Milestone,
  ScrollText,
  UserRound,
  UsersRound,
  Wrench,
} from "lucide-react";
import { matchPath } from "react-router";

import type { AppSummary, UserProfile } from "@/lib/api";
import { appIcon, appText, unreadBadge } from "@/lib/apps";
import { DOCS_PATH } from "@/lib/docs-pages";
import { i18n } from "@/lib/i18n";
import type { nl } from "@/lib/i18n/locales/nl";
import { isAdminRole, isDeveloper } from "@/lib/roles";

export interface NavItem {
  to: string;
  /** In the dashboard's language at the moment it is read. */
  label: string;
  icon: LucideIcon;
  /** Listed for admins only (the page sits behind `<AdminRoute>`). */
  adminOnly?: boolean;
  /** Listed for developers only (the page sits behind `<DeveloperRoute>`). */
  developerOnly?: boolean;
  /** A small count after the label, such as an app's unread meldingen: "3", "9+". */
  badge?: string | null;
  /** What the badge counts, in words: "3 ongelezen meldingen". */
  badgeLabel?: string | null;
}

export interface NavGroup {
  /** In the dashboard's language at the moment it is read. */
  label: string;
  items: NavItem[];
}

/** A navigation label's key under `nav.` in the texts. */
type NavKey = keyof typeof nl.nav;

/**
 * An object whose `label` is the `nav.<key>` text in the language of the
 * moment: a getter, so the shared definitions below follow a language switch
 * while keeping their plain `label: string` shape.
 */
function labelled<T extends object>(key: NavKey, rest: T): T & { readonly label: string } {
  return Object.defineProperty({ ...rest }, "label", {
    get: () => i18n.t(`nav.${key}`),
    enumerable: true,
  }) as T & { readonly label: string };
}

/** Mensen en teams, its first tab: the dashboard's accounts and their roles. */
export const PEOPLE_PATH = "/people";

/** Mensen en teams' second tab: who may use which tools and apps through the connector. */
export const TEAMS_PATH = "/people/teams";

/**
 * Every group of the sidebar, in order, with every item anyone can see.
 * Mensen en teams, the dashboard's accounts with their roles and who may use
 * what through the connector, sits right under Home: managing it is one of
 * the dashboard's main jobs for an admin. The Roadmap, what De Ceuster wants
 * from DC Data and the month's work against its budget, follows it, for
 * admins too.
 */
export const NAV_GROUPS: readonly NavGroup[] = [
  labelled("connector", {
    items: [
      labelled("home", { to: "/home", icon: House }),
      labelled("people", { to: PEOPLE_PATH, icon: UsersRound, adminOnly: true }),
      labelled("roadmap", { to: "/roadmap", icon: Milestone, adminOnly: true }),
      labelled("tools", { to: "/tools", icon: Wrench }),
      labelled("analytics", { to: "/analytics", icon: ChartColumn, adminOnly: true }),
    ],
  }),
];

/**
 * The sidebar's Apps group: one item per app the person may open, in the
 * registry's order, each to its own path with its icon and its unread
 * meldingen as a badge. Null without apps (none to open, or the list not
 * loaded yet), so the group is absent rather than empty.
 */
export function appsNavGroup(apps: readonly AppSummary[] | undefined): NavGroup | null {
  if (!apps || apps.length === 0) {
    return null;
  }
  return {
    label: i18n.t("nav.apps"),
    items: apps.map((app) => ({
      to: app.path,
      label: appText(app).title,
      icon: appIcon(app.icon),
      badge: unreadBadge(app.unread),
      badgeLabel: unreadBadge(app.unread)
        ? i18n.t("apps.unread", { count: app.unread ?? 0 })
        : null,
    })),
  };
}

/** The sidebar's groups with the Apps group under the first one, Connector; as they are without it. */
export function withAppsGroup(groups: readonly NavGroup[], apps: NavGroup | null): NavGroup[] {
  return apps ? [...groups.slice(0, 1), apps, ...groups.slice(1)] : [...groups];
}

/**
 * Pinned in the sidebar's footer, for everyone. It opens the docs' first page
 * and stays current on every page under `/docs`.
 */
export const DOCS_ITEM: NavItem = labelled("docs", { to: DOCS_PATH, icon: BookOpen });

/** Where the account menu's Settings opens: the first section of the Settings area. */
export const SETTINGS_PATH = "/settings/account";

/** Settings → Bekijken als (developers): the dashboard as a colleague or a role sees it. */
export const VIEW_AS_PATH = "/settings/view-as";

/**
 * Every group of the Settings area's own navigation, in order. Organization
 * (the activity log) is for admins only, Developers (Bekijken als) for
 * developers only; the dashboard's accounts and the connector's teams have
 * their own page in the sidebar, Mensen en teams.
 */
export const SETTINGS_GROUPS: readonly NavGroup[] = [
  labelled("personal", {
    items: [labelled("account", { to: SETTINGS_PATH, icon: UserRound })],
  }),
  labelled("organization", {
    items: [labelled("activity", { to: "/settings/activity", icon: ScrollText, adminOnly: true })],
  }),
  labelled("developer", {
    items: [labelled("viewAs", { to: VIEW_AS_PATH, icon: Eye, developerOnly: true })],
  }),
  labelled("general", {
    items: [labelled("about", { to: "/settings/about", icon: Info })],
  }),
];

/**
 * The groups someone sees: admins everything but the developer-only items
 * (`developer` adds those), everyone else without the admin-only items too.
 * A group left without items is dropped, label and all.
 */
export function visibleNavGroups(
  isAdmin: boolean,
  groups = NAV_GROUPS,
  developer = false,
): NavGroup[] {
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) => (isAdmin || !item.adminOnly) && (developer || !item.developerOnly),
      ),
    }))
    .filter((group) => group.items.length > 0);
}

/**
 * The Settings sections someone with `role` sees: Organization for an admin
 * (an owner and a developer are admins too), Developers for a developer, but
 * not while they view the dashboard as someone else (`viewing`), a developer
 * included.
 */
export function visibleSettingsGroups(
  role: string | null | undefined,
  viewing = false,
): NavGroup[] {
  return visibleNavGroups(isAdminRole(role), SETTINGS_GROUPS, isDeveloper(role) && !viewing);
}

/**
 * Whether an item of the sidebar or an area's own navigation is the page
 * that is open, from the router's pathname. Prefix matching, whole segments
 * only: an item stays current on the pages under it (Handleiding on
 * `/docs/limits`), and `/docs` is not current on `/docs-old`.
 */
export function isNavItemCurrent(item: Pick<NavItem, "to">, pathname: string): boolean {
  return matchPath({ path: item.to, end: false }, pathname) !== null;
}

type Account = Pick<UserProfile, "email" | "first_name" | "last_name">;

/** Someone's first and last name, trimmed, or null when they have neither. */
export function fullName(person: Pick<UserProfile, "first_name" | "last_name">): string | null {
  const name = [person.first_name, person.last_name]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
  return name || null;
}

/** The name in the account menu: first and last name, or the email without them. */
export function accountName(account: Account): string {
  return fullName(account) ?? account.email;
}

/** The avatar's letter: the first name's initial, or the email's, upper case. */
export function accountInitial(account: Account): string {
  const source = account.first_name?.trim() || account.email.trim();
  // Array.from splits by code point, so a name starting outside the BMP stays whole.
  return (Array.from(source)[0] ?? "?").toUpperCase();
}
